/*
  The phone shell: a slim top bar with the portal logo (same file and height as the portal's
  mobile header) and a bottom tab bar in the portal's colours. The portal has only a drawer menu,
  so the tab bar is new, but every colour and font comes from the portal tokens.
*/
import { CalendarOutlined, HomeOutlined, MessageOutlined, SettingOutlined } from "@ant-design/icons";
import logo from "app/assets/portal/logo-classtutor.png";
import { useGetChatQuery } from "app/services/chat";
import { useChatStream } from "app/services/chatStream";
import { useReach } from "app/native/useReach";
import storage, { STORAGE_KEYS } from "app/utils/storage";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import AlarmOverlay from "./AlarmOverlay";
import { Button } from "./v1/Button";

export function TopBar({ title, right }: { title?: string; right?: React.ReactNode }) {
  return (
    <header className="d-flex align-items-center justify-content-between" style={{ paddingTop: "var(--ct-safe-top)", minHeight: 56, marginBottom: 8 }}>
      {title ? <h1 className="m-0" style={{ fontSize: 26, letterSpacing: -1 }}>{title}</h1> : <img src={logo} alt="ClassTutor" height={30} />}
      <div>{right}</div>
    </header>
  );
}

export default function Shell() {
  const location = useLocation();
  const live = useChatStream(true);
  const reach = useReach();
  // The stream delivers new messages; polling is only the fallback while it is down.
  const { data: chat } = useGetChatQuery(undefined, { pollingInterval: live ? 60000 : location.pathname.startsWith("/messages") ? 5000 : 20000, refetchOnFocus: true, refetchOnReconnect: true });
  const readUpTo = Number(storage.getItem(STORAGE_KEYS.CHAT_READ) || 0);
  const unread = (chat?.messages ?? []).filter((m) => m.kind === "out" && m.id > readUpTo).length;

  return (
    <>
      {reach.missing && (
        <div className="ct-reach" role="status">
          <span><b>Your notifications are not reaching this device.</b> The last few did not come through.</span>
          <span className="d-flex gap-2 flex-shrink-0">
            <Link to="/settings"><Button size="sm" buttonType="white" name="Fix it" /></Link>
            <Button size="sm" buttonType="text" style={{ color: "#fff" }} name="Not now" onClick={reach.snooze} />
          </span>
        </div>
      )}
      <Outlet />
      <nav className="ct-nav" aria-label="Main">
        <NavLink to="/" end><HomeOutlined /><span>Home</span></NavLink>
        <NavLink to="/timetable"><CalendarOutlined /><span>Timetable</span></NavLink>
        <NavLink to="/messages"><MessageOutlined />{unread > 0 && <span className="ct-dot">{unread}</span>}<span>Messages</span></NavLink>
        <NavLink to="/settings"><SettingOutlined /><span>Settings</span></NavLink>
      </nav>
      <AlarmOverlay />
    </>
  );
}
